// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export const GLOSSARY: Readonly<Record<string, { title: string; body: string }>> = {
  'tag-character': {
    title: 'Tag character',
    body: 'A code point in the range U+E0020 to U+E007E. Each one mirrors a printable ASCII character exactly — subtract 0xE0000 and you have the letter — but every editor, terminal, browser, diff and code-review page draws it as nothing at all. A language model reading the raw text sees ordinary words.',
  },
  'variation-selector': {
    title: 'Variation selector',
    body: 'A modifier that changes how the character before it is drawn — most familiarly, it is what makes an emoji render in colour rather than as a black-and-white symbol. There are exactly 256 of them, which is exactly one byte of range, so a chain of them after any character can carry an arbitrary byte string invisibly.',
  },
  'zero-width': {
    title: 'Zero width',
    body: 'A character that occupies no space. Some are essential — a zero-width joiner is what makes several emoji into one, and a zero-width non-joiner is required to spell Persian correctly — and some are there only to break a comparison, split a keyword past a filter, or carry a hidden bit.',
  },
  bidi: {
    title: 'Bidirectional control',
    body: 'A character that changes the direction text is laid out in, so that what a human reads and what a compiler reads can be two different things. Legitimate and necessary in Arabic and Hebrew; the basis of the Trojan Source attack when a control is opened and never closed.',
  },
  homoglyph: {
    title: 'Homoglyph',
    body: 'A character that looks like another one. Cyrillic а and Latin a are different code points that most fonts draw identically, so two identifiers, domains or package names can be visually identical and completely different strings.',
  },
  'default-ignorable': {
    title: 'Default ignorable',
    body: 'A Unicode property meaning "conforming software should render nothing for this". It is a useful fact to report and a bad list to clean from: about half of it is essential to some language, and it deliberately excludes the whole whitespace family, which is what actually breaks most files.',
  },
  skeleton: {
    title: 'Skeleton',
    body: 'The Unicode-defined comparison form of a string: every character replaced by a representative of its lookalike group. Two strings are confusable exactly when their skeletons match. It is a comparison key rather than a reading — it folds m to rn, for instance — so charsight shows you a plain-ASCII fold instead.',
  },
  'render-mode': {
    title: 'Text rendering mode',
    body: 'A PDF instruction that says how to paint text. Mode 3 means "do not paint it at all". It is the entirely ordinary way a scanned page carries a searchable text layer over the image — and also the simplest way to put text in a document that nobody will see.',
  },
};

/**
 * Click-to-define tooltips.
 *
 * Dismissal follows the same contract as every other overlay: Escape, a pointer anywhere outside,
 * and re-clicking the term itself. One event family only.
 */
export function installGlossary(): void {
  const tooltip = document.getElementById('tooltip');
  if (!tooltip) return;
  let current: HTMLElement | null = null;

  const hide = (): void => {
    tooltip.setAttribute('hidden', '');
    current = null;
  };

  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link[data-term]');
    if (!target) {
      if (current && !tooltip.contains(e.target as Node)) hide();
      return;
    }
    const term = target.dataset['term'];
    const entry = term ? GLOSSARY[term] : undefined;
    if (!entry) return;
    if (current === target) {
      hide();
      return;
    }
    e.preventDefault();
    tooltip.replaceChildren();
    const h = document.createElement('strong');
    h.textContent = entry.title;
    tooltip.append(h, document.createTextNode(entry.body));
    tooltip.removeAttribute('hidden');
    const r = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
    const below = r.bottom + 8;
    tooltip.style.top =
      below + tooltip.offsetHeight > window.innerHeight
        ? `${Math.max(12, r.top - tooltip.offsetHeight - 8)}px`
        : `${below}px`;
    current = target;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) hide();
  });
  window.addEventListener('resize', hide);
}
